/* eslint-disable @next/next/no-img-element */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Draggable } from '@fullcalendar/interaction';
dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Lib
import { POST_STATUS, DRAFT } from '@/lib/scheduler/postStatus';
import { POST_TYPE } from '@/lib/scheduler/postType';

// Components
import Icon from '@/components/base/Icon';
import SchedulerPostSettingsBg from '@/components/icons/SchedulerPostSettingsBg';
import { CaptionPreview } from '@/components/composite/CaptionPreview';
import SocialProfileMiniDisplay from '@/components/composite/SocialProfiles/SocialProfileMiniDisplay';
import Tooltip from '@/components/base/Tooltip';

const PostEvent = forwardRef(
    (
        {
            post,
            timeFormat,
            timezone,
            isStacked = false,
            isReadOnly = false,
            classNameWrapper,
            stackedPosts,
            settings = null,
            clickAction = {
                isAvailable: false,
                edit: () => {
                    /** */
                },
                preview: () => {
                    /** */
                }
            },
            isPlaceholder = false,
            isGroup,
            ...restProps
        },
        ref
    ) => {
        const dateNow = dayjs().utc().tz(timezone).format();
        const isFuture = dayjs(post.scheduled_at).utc().tz(timezone).isAfter(dateNow);
        const settingsRef = useRef(null);

        useEffect(() => {
            if (!isStacked || isReadOnly) return;
            new Draggable(ref?.current, {
                eventData: post
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <PostEvent.Wrapper
                isFuture={isFuture}
                isReadOnly={isReadOnly}
                classNameWrapper={classNameWrapper}
                onClick={(e) => {
                    if (post?.read_only) return;

                    if (!clickAction?.isAvailable) return;
                    // check if click is on '...'
                    if (settingsRef?.current?.contains(e?.target)) return;

                    if (!isFuture) {
                        return clickAction.preview(post);
                    }
                    return clickAction?.edit(post);
                }}
                {...restProps}
                ref={ref}>
                <div className="flex w-full">
                    <div className="relative w-full">
                        <PostEvent.Content
                            post={post}
                            network={post?.social_profile?.social_network_type}
                            isPlaceholder={isPlaceholder}
                        />
                        <div className="absolute left-0 top-0 flex h-full w-full">
                            <PostEvent.Status status={post.state} />
                            <div className="flex w-full flex-col justify-between p-0.5">
                                <div className="flex">
                                    {!isPlaceholder ? (
                                        <PostEvent.DateType
                                            date={post.scheduled_at}
                                            type={post.type}
                                            timeFormat={timeFormat}
                                            timezone={timezone}
                                        />
                                    ) : null}
                                </div>
                                <div
                                    className={clsx('flex w-full', {
                                        'flex-row-reverse items-end justify-between': isGroup,
                                        'items-center justify-end': !isGroup
                                    })}>
                                    {isGroup ? (
                                        <SocialProfileMiniDisplay
                                            socialProfile={post?.social_profile}
                                        />
                                    ) : null}
                                    <div className="flex space-x-0.5">
                                        <PostEvent.AutoPost
                                            status={post.state}
                                            autopost={post?.autopost ?? false}
                                            validatedAt={post?.validated_at ?? null}
                                        />
                                        <PostEvent.CrossPost
                                            hasCrossPostProfiles={
                                                (post?.cross_post_id &&
                                                    Boolean(
                                                        post?.cross_post_social_profiles?.length
                                                    )) ||
                                                post?.master_id
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {settings && !isReadOnly ? (
                            <PostEvent.Settings
                                settingsContent={settings}
                                settingsRef={settingsRef}
                            />
                        ) : null}
                    </div>
                    {stackedPosts?.length > 1 ? (
                        <div className="flex items-center bg-icoGray-300 p-1 font-GSemiBold text-9 text-icoBlack dark:bg-icoDarkMode-goudron dark:text-white">
                            +{stackedPosts?.length - 1}
                        </div>
                    ) : null}
                </div>
            </PostEvent.Wrapper>
        );
    }
);

PostEvent.displayName = 'PostEvent';

PostEvent.propTypes = {
    post: PropTypes.object.isRequired,
    isReadOnly: PropTypes.bool,
    timeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    classNameWrapper: PropTypes.string,
    stackedPosts: PropTypes.array,
    settings: PropTypes.node,
    clickAction: PropTypes.shape({
        isAvailable: PropTypes.bool,
        edit: PropTypes.func,
        preview: PropTypes.func
    }),
    isPlaceholder: PropTypes.bool,
    isGroup: PropTypes.bool,
    isStacked: PropTypes.bool
};

/**
 * Wrapper props
 */
const Wrapper = forwardRef(
    ({ isFuture, classNameWrapper, children, isReadOnly, ...restProps }, ref) => {
        const { t } = useTranslation('scheduler');
        return (
            <div
                ref={ref}
                className={clsx('relative flex h-12 w-full p-0.5', classNameWrapper)}
                {...restProps}>
                <Tooltip
                    content={isReadOnly ? t('scheduler:calendar.post_disabled_multi_post') : null}>
                    <div
                        className={clsx(
                            'group relative flex h-full w-full cursor-pointer overflow-hidden rounded-m bg-icoGray-300 p-0.5 dark:bg-icoDarkMode-goudron',
                            { 'opacity-40': !isFuture }
                        )}>
                        {children}
                        {isReadOnly ? (
                            <div className="absolute left-0 top-0 z-1 flex h-full w-full items-center justify-center p-0.5">
                                <div className="h-full w-full rounded-m bg-icoGray-400/60" />
                            </div>
                        ) : null}
                    </div>
                </Tooltip>
            </div>
        );
    }
);

Wrapper.propTypes = {
    isFuture: PropTypes.bool,
    classNameWrapper: PropTypes.string,
    children: PropTypes.node
};

Wrapper.displayName = 'PostEvent.Wrapper';
PostEvent.Wrapper = Wrapper;

/**
 * Content:
 * - Display Image if there is a media
 * - Display Caption if there is no media but a caption
 * - Display Icon media if there is no media and no caption
 */
const styleContentNoImage =
    'bg-icoGray-100 dark:bg-icoDarkMode-goudron text-icoBlack dark:text-white';
const stylePlaceholder = 'bg-icoGray-400 dark:bg-icoDarkMode-goudron/40 text-white';
const Content = ({ post, network, isPlaceholder }) => {
    const [imgSrc, setImgSrc] = useState(
        post?.data?.medias?.[0]?.min_thumbnail ??
            post?.data?.medias?.[0]?.image ??
            post?.data?.medias?.[0]?.thumbnail ??
            ''
    );

    if (post?.data?.medias && post?.data?.medias?.length && imgSrc !== '') {
        return (
            <img
                src={imgSrc}
                alt=""
                className="h-full w-full rounded-m object-cover"
                onError={() => {
                    if (
                        post?.data?.medias?.[0]?.min_thumbnail &&
                        imgSrc === post?.data?.medias?.[0]?.min_thumbnail
                    ) {
                        setImgSrc(post?.data?.medias?.[0]?.image);
                    } else if (
                        post?.data?.medias?.[0]?.image &&
                        imgSrc === post?.data?.medias?.[0]?.image
                    ) {
                        setImgSrc(post?.data?.medias?.[0]?.thumbnail ?? '');
                    } else {
                        setImgSrc('');
                    }
                }}
            />
        );
    }

    if (post?.data?.caption && post?.data?.caption !== '') {
        return (
            <div
                className={`absolute inset-0 bg-icoGray-100 pl-2 pt-m font-GMedium text-xxs text-icoBlack dark:bg-icoDarkMode-goudron dark:text-white ${styleContentNoImage}`}>
                <CaptionPreview
                    as="span"
                    className="line-clamp-1"
                    caption={post?.data?.caption ?? ''}
                    network={network}
                    mentions={post?.data?.mentions ?? []}
                />
                <span className="absolute bottom-0 left-0 right-0 block h-full w-full bg-gradient-to-t from-icoGray-100/100 to-icoGray-100/0 dark:from-icoDarkMode-goudron/100 dark:to-icoDarkMode-goudron/0" />
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'absolute inset-0 flex items-center justify-center',
                isPlaceholder ? stylePlaceholder : styleContentNoImage,
                {
                    'text-[44px]': !isPlaceholder
                }
            )}>
            <Icon
                icon={isPlaceholder ? 'plus' : 'content'}
                className={clsx({
                    'text-icoGray-400 opacity-40': !isPlaceholder
                })}
                data-testid="no-media-no-caption"
            />
        </div>
    );
};

Content.propTypes = {
    post: PropTypes.object.isRequired,
    network: PropTypes.string,
    isPlaceholder: PropTypes.bool
};

PostEvent.Content = Content;

/**
 * Status:
 * - Schedule
 * - Draft
 * - Approved
 * - Waiting for approval
 * - Rejected
 */
const Status = ({ status }) => {
    return (
        <div
            className="relative h-full w-1 overflow-hidden rounded-bl-m rounded-tl-m text-xxs text-white transition-all group-hover:w-4"
            style={{ backgroundColor: POST_STATUS[status]?.color }}>
            <Icon
                icon={POST_STATUS[status]?.icon ?? ''}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform opacity-0 transition-opacity group-hover:opacity-100"
            />
        </div>
    );
};

Status.propTypes = {
    status: PropTypes.string.isRequired
};
PostEvent.Status = Status;

/**
 * Display date et type for a post
 */
const DateType = ({ date, type, timeFormat, timezone }) => {
    const hourPost = dayjs(date)
        .utc()
        .tz(timezone)
        .format(timeFormat === '12' ? 'h:mm a' : 'H:mm');

    return (
        <div className="flex items-center space-x-2 rounded-sm bg-icoBlack/60 px-1 py-0.75 text-8 leading-none text-white">
            {type ? <Icon icon={POST_TYPE[type]?.icon ?? ''} /> : null}
            <span>{hourPost}</span>
        </div>
    );
};

DateType.propTypes = {
    date: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    timeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired
};
PostEvent.DateType = DateType;

/**
 * AutoPost tag
 */
const AutoPost = ({ autopost, validatedAt, status }) => {
    const { t } = useTranslation('scheduler');
    if (!autopost || status === DRAFT) {
        return null;
    }

    return (
        <div
            className={clsx('flex items-center rounded-[1px] p-0.5', {
                'bg-icoYellow-100 text-icoYellow': validatedAt,
                'bg-icoGray-400 text-white': !validatedAt
            })}
            title={t('auto_post_tag_extended')}>
            <Icon
                icon={validatedAt ? 'autopost' : 'autopost-no'}
                className="text-8"
                data-testid="icon-autopost"
            />
        </div>
    );
};

AutoPost.propTypes = {
    autopost: PropTypes.bool,
    validatedAt: PropTypes.string,
    status: PropTypes.string
};
PostEvent.AutoPost = AutoPost;

/**
 * Cross post tag
 */
const CrossPost = ({ hasCrossPostProfiles }) => {
    const { t } = useTranslation('scheduler');
    if (!hasCrossPostProfiles) {
        return null;
    }

    return (
        <div
            className="flex items-center rounded-[1px] bg-icoAquaBlue-100 p-0.5 text-8 leading-none text-icoAquaBlue"
            title={t('cross_post_tag_extended')}>
            <Icon icon="crossposts" data-testid="icon-crossposts" />
        </div>
    );
};

CrossPost.propTypes = {
    hasCrossPostProfiles: PropTypes.bool
};
PostEvent.CrossPost = CrossPost;

const Settings = ({ settingsContent, settingsRef }) => {
    const { isDarkMode } = useDarkMode();

    return (
        <div ref={settingsRef}>
            <SchedulerPostSettingsBg
                className="absolute right-0 top-0 z-1 rounded-tr-m"
                darkMode={isDarkMode()}
            />
            {settingsContent}
        </div>
    );
};

Settings.propTypes = {
    settingsContent: PropTypes.node,
    settingsRef: PropTypes.object
};
PostEvent.Settings = Settings;

export default PostEvent;
